import {stripTagsAndEntities} from '@/utils/string';
import {isLegacyCompletionRow, selectWritableCompletions} from '@/service/wholeArticleApply';
import {generationInProgress, savedRunPastLimit} from '@/service/generationSaveGuard';

/**
 * A generation save must not persist an article that is behind the completion.
 * Fewer H2 slots with text, or fewer words in a section the completion already
 * filled, means the in-memory article has not caught up yet.
 * The guard applies only while generation owns the article: the run is in
 * progress, path B review waits for the user, or the write assembles the
 * generation result before the first confirmed done. A manual save after done
 * writes what the editor shows. Legacy completion rows (no `state`) never count.
 * When the write assembles the generation result, FAQ rows with an answer are
 * copied onto it and a question with no answer text is omitted. Done waits
 * until the document contains the answered FAQ. URL-improve keeps an existing
 * meta title and a heading the improve path already settled, and still refuses
 * an empty slot.
 */

export const COMPLETION_PARTS = ['meta', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'question'] as const;

export type CompletionPart = typeof COMPLETION_PARTS[number];

export type CompletionRow = {
    value1?: string | null;
    value2?: string | null;
    keyword?: string | null;
    state?: string;
    seq?: number;
};

export type CompletionMap = Partial<Record<CompletionPart, CompletionRow[] | null | undefined>>;

export type HeadingBlock = {
    h?: string | null;
    p?: string | null;
};

export type QuestionBlock = {
    question?: string | null;
    answer?: string | null;
};

export type SaveArticleBody = {
    metaTitle?: string | null;
    metaDescription?: string | null;
    h1?: HeadingBlock[] | null;
    h2?: HeadingBlock[] | null;
    h3?: HeadingBlock[] | null;
    h4?: HeadingBlock[] | null;
    h5?: HeadingBlock[] | null;
    h6?: HeadingBlock[] | null;
    questions?: QuestionBlock[] | null;
    /** URL-improve. Whole mode leaves this unset. */
    isAnalyze?: boolean;
    enhanced?: string | null;
    aiGenerateStatus?: string | null;
};

export type CompletionLag =
    | {reason: 'h2-count'; articleH2: number; completionH2: number}
    | {
        reason: 'section-words';
        part: CompletionPart;
        index: number;
        articleWords: number;
        completionWords: number;
        articleBodyWords: number;
        completionBodyWords: number;
    };

export class SaveIntegrityError extends Error {
    readonly code = 'save-integrity';

    constructor() {
        super('save-integrity');
        this.name = 'SaveIntegrityError';
    }
}

export function isSaveIntegrityError(error: unknown): boolean {
    return !!error && typeof error === 'object' && (error as {code?: string}).code === 'save-integrity';
}

export function sectionWordCount(heading: unknown, body: unknown): number {
    const text = `${stripTagsAndEntities(String(heading || ''))} ${stripTagsAndEntities(String(body || ''))}`.trim();
    if (!text) {
        return 0;
    }
    return text.split(/\s+/).filter(Boolean).length;
}

export function plainSectionText(value: unknown): string {
    return stripTagsAndEntities(String(value || '')).replace(/\s+/g, ' ').trim().toLowerCase();
}

function textCompletions(rows: CompletionRow[] | null | undefined, part: string): CompletionRow[] {
    const current = (rows || []).filter((row) => !isLegacyCompletionRow(row));
    return selectWritableCompletions(current, part).filter((row) => {
        if (part === 'question') {
            return sectionWordCount('', row.value2) > 0;
        }
        return sectionWordCount(row.value1, row.value2) > 0;
    });
}

export function completionMapHasText(completions: CompletionMap | null | undefined): boolean {
    if (!completions) {
        return false;
    }
    return COMPLETION_PARTS.some((part) => textCompletions(completions[part], part).length > 0);
}

function headingBlocks(article: SaveArticleBody | null | undefined, part: CompletionPart): HeadingBlock[] {
    if (!article || part === 'meta' || part === 'question') {
        return [];
    }
    const value = article[part];
    return Array.isArray(value) ? value : [];
}

export function filledH2Count(article: SaveArticleBody | null | undefined): number {
    return headingBlocks(article, 'h2').filter((block) => sectionWordCount(block?.h, block?.p) > 0).length;
}

function matchHeading(blocks: HeadingBlock[], row: CompletionRow, index: number): HeadingBlock | null {
    const keys = [plainSectionText(row.value1), plainSectionText(row.keyword)].filter((key) => key.length > 0);
    for (const key of keys) {
        const found = blocks.find((block) => {
            const heading = plainSectionText(block?.h);
            return heading.length > 0 && heading === key;
        });
        if (found) {
            return found;
        }
    }
    return blocks[index] || null;
}

function matchQuestion(questions: QuestionBlock[], row: CompletionRow, index: number): QuestionBlock | null {
    const keys = [plainSectionText(row.value1), plainSectionText(row.keyword)].filter((key) => key.length > 0);
    for (const key of keys) {
        const found = questions.find((item) => {
            const question = plainSectionText(item?.question);
            return question.length > 0 && question === key;
        });
        if (found) {
            return found;
        }
    }
    return questions[index] || null;
}

function sectionDetail(
    article: SaveArticleBody,
    part: CompletionPart,
    row: CompletionRow,
    index: number,
): {words: number; bodyWords: number} {
    if (part === 'meta') {
        return {
            words: sectionWordCount(article.metaTitle, article.metaDescription),
            bodyWords: sectionWordCount('', article.metaDescription),
        };
    }
    if (part === 'question') {
        const questions = Array.isArray(article.questions) ? article.questions : [];
        const matched = matchQuestion(questions, row, index);
        return {
            words: matched ? sectionWordCount(matched.question, matched.answer) : 0,
            bodyWords: matched ? sectionWordCount('', matched.answer) : 0,
        };
    }
    const matched = matchHeading(headingBlocks(article, part), row, index);
    return {
        words: matched ? sectionWordCount(matched.h, matched.p) : 0,
        bodyWords: matched ? sectionWordCount('', matched.p) : 0,
    };
}

/** Every way the article is shorter than completion text that already exists. */
export function completionLags(
    article: SaveArticleBody | null | undefined,
    completions: CompletionMap | null | undefined,
): CompletionLag[] {
    const body = article || {};
    const map = completions || {};
    const lags: CompletionLag[] = [];
    const completionH2 = textCompletions(map.h2, 'h2');
    const articleH2 = filledH2Count(body);
    if (articleH2 < completionH2.length) {
        lags.push({reason: 'h2-count', articleH2, completionH2: completionH2.length});
    }
    for (const part of COMPLETION_PARTS) {
        const rows = textCompletions(map[part], part);
        rows.forEach((row, index) => {
            const completionWords = sectionWordCount(row.value1, row.value2);
            const completionBodyWords = sectionWordCount('', row.value2);
            const detail = sectionDetail(body, part, row, index);
            if (detail.words < completionWords) {
                lags.push({
                    reason: 'section-words',
                    part,
                    index,
                    articleWords: detail.words,
                    completionWords,
                    articleBodyWords: detail.bodyWords,
                    completionBodyWords,
                });
            }
        });
    }
    return lags;
}

function textRows(completions: CompletionMap | null | undefined, part: CompletionPart): CompletionRow[] {
    return textCompletions(completions?.[part], part);
}

function textMatchHeading(blocks: HeadingBlock[], row: CompletionRow): HeadingBlock | null {
    const keys = [plainSectionText(row.value1), plainSectionText(row.keyword)].filter((key) => key.length > 0);
    for (const key of keys) {
        const found = blocks.find((block) => {
            const heading = plainSectionText(block?.h);
            return heading.length > 0 && heading === key;
        });
        if (found) {
            return found;
        }
    }
    return null;
}

function rowSettled(row: CompletionRow | null | undefined): boolean {
    return row?.state === 'done';
}

function emptyPlaceholder(block: HeadingBlock | null | undefined): boolean {
    if (!block) {
        return false;
    }
    return plainSectionText(block.h).length === 0 && sectionWordCount(block.h, block.p) === 0;
}

/**
 * URL-improve already has a page title, and setAiMeta does not replace it.
 * A settled heading the improve path did not apply, or a non-empty section it
 * kept shorter than the raw completion, is not an unfinished slot.
 * An empty placeholder and a whole-mode article stay strict.
 */
function relaxAnalyzeLag(
    article: SaveArticleBody,
    completions: CompletionMap | null | undefined,
    lag: CompletionLag,
): boolean {
    if (!article.isAnalyze) {
        return false;
    }
    if (lag.reason === 'h2-count') {
        const rows = textRows(completions, 'h2');
        if (!rows.length || !rows.every((row) => rowSettled(row))) {
            return false;
        }
        const blocks = headingBlocks(article, 'h2');
        return rows.every((row, index) => {
            if (textMatchHeading(blocks, row)) {
                return true;
            }
            return !emptyPlaceholder(blocks[index]);
        });
    }
    if (lag.reason !== 'section-words' || lag.part === 'question') {
        return false;
    }
    const row = textRows(completions, lag.part)[lag.index];
    if (!rowSettled(row)) {
        return false;
    }
    if (lag.part === 'meta') {
        return sectionWordCount(article.metaTitle, article.metaDescription) > 0;
    }
    if (lag.articleWords > 0 && !(lag.completionBodyWords > 0 && lag.articleBodyWords === 0)) {
        return true;
    }
    const blocks = headingBlocks(article, lag.part);
    if (textMatchHeading(blocks, row)) {
        return false;
    }
    return !emptyPlaceholder(blocks[lag.index]);
}

function blockingLags(
    article: SaveArticleBody | null | undefined,
    completions: CompletionMap | null | undefined,
): CompletionLag[] {
    return completionLags(article, completions).filter((lag) => !article || !relaxAnalyzeLag(article, completions, lag));
}

export function articleLagsCompletion(
    article: SaveArticleBody | null | undefined,
    completions: CompletionMap | null | undefined,
): boolean {
    return blockingLags(article, completions).length > 0;
}

function dropsStoredFilledSection(
    candidate: SaveArticleBody | null | undefined,
    stored: SaveArticleBody | null | undefined,
): boolean {
    if (!stored) {
        return false;
    }
    if (filledH2Count(candidate) < filledH2Count(stored)) {
        return true;
    }
    const candidateBlocks = headingBlocks(candidate, 'h2');
    for (const block of headingBlocks(stored, 'h2')) {
        if (sectionWordCount(block?.h, block?.p) === 0) {
            continue;
        }
        const found = candidateBlocks.find((item) => {
            const heading = plainSectionText(item?.h);
            return heading.length > 0 && heading === plainSectionText(block?.h);
        });
        if (!found || sectionWordCount('', found.p) === 0) {
            return true;
        }
    }
    return false;
}

function dropsStoredFaq(
    candidate: SaveArticleBody | null | undefined,
    stored: SaveArticleBody | null | undefined,
): boolean {
    if (!stored) {
        return false;
    }
    const candidateQuestions = Array.isArray(candidate?.questions) ? candidate.questions : [];
    for (const item of Array.isArray(stored.questions) ? stored.questions : []) {
        if (sectionWordCount('', item?.answer) === 0) {
            continue;
        }
        const key = plainSectionText(item?.question);
        if (!key) {
            continue;
        }
        const found = candidateQuestions.find((row) => plainSectionText(row?.question) === key);
        if (!found || sectionWordCount('', found.answer) === 0) {
            return true;
        }
    }
    return false;
}

function answeredQuestion(item: QuestionBlock | null | undefined): boolean {
    return sectionWordCount('', item?.answer) > 0 && plainSectionText(item?.question).length > 0;
}

/**
 * Copy settled FAQ from the completion when the article row is missing or shorter.
 * A question completion with no answer text is omitted. A longer answer already
 * on the article stays.
 */
export function withCompletionFaq<T extends SaveArticleBody>(
    article: T,
    completions: CompletionMap | null | undefined,
): T {
    const rows = textRows(completions, 'question').filter((row) => rowSettled(row) && sectionWordCount('', row.value2) > 0);
    const existing = (Array.isArray(article.questions) ? article.questions : []).filter(answeredQuestion);
    if (!rows.length) {
        if (existing.length === (article.questions || []).length) {
            return article;
        }
        return {...article, questions: existing};
    }
    const used = new Set<QuestionBlock>();
    const questions: QuestionBlock[] = [];
    const seen = new Set<string>();
    const pushQuestion = (item: QuestionBlock) => {
        const key = plainSectionText(item.question);
        if (!key || seen.has(key) || !answeredQuestion(item)) {
            return;
        }
        seen.add(key);
        questions.push(item);
    };
    rows.forEach((row, index) => {
        const matched = matchQuestion(existing, row, index);
        const completionWords = sectionWordCount(row.value1, row.value2);
        const articleWords = matched ? sectionWordCount(matched.question, matched.answer) : 0;
        if (matched && articleWords >= completionWords) {
            used.add(matched);
            pushQuestion(matched);
            return;
        }
        pushQuestion({
            question: row.value1 || row.keyword || '',
            answer: row.value2 || '',
        });
    });
    for (const item of existing) {
        if (used.has(item)) {
            continue;
        }
        pushQuestion(item);
    }
    return {...article, questions};
}

export function articleGenerationDone(article: {enhanced?: string | null; aiGenerateStatus?: string | null} | null | undefined): boolean {
    return article?.enhanced === 'done' || article?.aiGenerateStatus === 'done';
}

/**
 * This tab still holds running past the run limit, so it never saw the run
 * end, and its content is from before the run. When the stored article is
 * done, the server committed the generated article there: the save would
 * write the old content over it, and with done nothing would bring the
 * generated text back. The save is held instead (#30937 review).
 */
export function staleRunSaveOverwritesDone(
    article: Parameters<typeof savedRunPastLimit>[0],
    stored: {enhanced?: string | null; aiGenerateStatus?: string | null} | null | undefined,
    now: number = Date.now(),
): boolean {
    return savedRunPastLimit(article, now) && articleGenerationDone(stored);
}

export type SaveIntent = {
    /**
     * The write assembles the generation result and may confirm done
     * (commitCoveringArticle with markDone).
     */
    assemblesGeneration?: boolean;
};

/**
 * The completion guard holds a write only while generation owns the article:
 * the run is in progress, path B review still waits for the user, or the write
 * assembles the generation result before the first confirmed done.
 * A manual save after done, after a failed run, or of an article that was
 * never generated is written as the editor shows it.
 */
export function generationGuardsSave(
    stored: {enhanced?: string | null; aiGenerateStatus?: string | null} | null | undefined,
    intent?: SaveIntent,
): boolean {
    if (generationInProgress(stored)) {
        return true;
    }
    if (articleGenerationDone(stored)) {
        return false;
    }
    if (stored?.aiGenerateStatus === 'review') {
        return true;
    }
    return !!intent?.assemblesGeneration;
}

function storedQuestionKeys(stored: SaveArticleBody | null | undefined): Set<string> {
    return new Set(
        (Array.isArray(stored?.questions) ? stored.questions : [])
            .map((item) => plainSectionText(item?.question))
            .filter((key) => key.length > 0),
    );
}

/**
 * A question completion that is not already on a done article must not block
 * a later save, and it must not be appended either.
 */
function lagsThatMayBlock(
    candidate: SaveArticleBody | null | undefined,
    stored: SaveArticleBody | null | undefined,
    completions: CompletionMap | null | undefined,
): CompletionLag[] {
    const lags = blockingLags(candidate, completions);
    if (!articleGenerationDone(stored)) {
        return lags;
    }
    const keys = storedQuestionKeys(stored);
    const rows = textRows(completions, 'question');
    return lags.filter((lag) => {
        if (lag.reason !== 'section-words' || lag.part !== 'question') {
            return true;
        }
        const row = rows[lag.index];
        const key = plainSectionText(row?.value1) || plainSectionText(row?.keyword);
        return !key || keys.has(key);
    });
}

/**
 * Refuse a write that would persist a short body over completion text while
 * generation owns the article. A later edit may shorten wording only after the
 * stored article already covers the completion, and only when it does not
 * clear a filled section. Outside generation every write passes.
 */
export function shouldWriteArticle(
    candidate: SaveArticleBody | null | undefined,
    stored: SaveArticleBody | null | undefined,
    completions: CompletionMap | null | undefined,
    intent?: SaveIntent,
): boolean {
    if (!generationGuardsSave(stored, intent)) {
        return true;
    }
    if (!completionMapHasText(completions)) {
        return true;
    }
    if (dropsStoredFilledSection(candidate, stored) || dropsStoredFaq(candidate, stored)) {
        return false;
    }
    // Path B keeps the scraped body until the user accepts a proposal.
    // Completion text is staged, so a shorter original section is not a lag.
    // The save that settles the last block moves review to done in the same write.
    if (candidate?.isAnalyze && stored?.isAnalyze && stored.aiGenerateStatus === 'review'
        && (candidate.aiGenerateStatus === 'review'
            || (candidate.aiGenerateStatus === 'done' && candidate.enhanced === 'done'))) {
        return true;
    }
    const lags = lagsThatMayBlock(candidate, stored, completions);
    if (lags.length === 0) {
        return true;
    }
    if (!stored || lagsThatMayBlock(stored, stored, completions).length > 0) {
        return false;
    }
    if (lags.some((lag) => lag.reason === 'h2-count')) {
        return false;
    }
    return lags.every((lag) => (
        lag.reason === 'section-words'
        && lag.articleWords > 0
        && !(lag.completionBodyWords > 0 && lag.articleBodyWords === 0)
    ));
}

export function h2WordCount(article: SaveArticleBody | null | undefined): number {
    return headingBlocks(article, 'h2').reduce((sum, block) => sum + sectionWordCount(block?.h, block?.p), 0);
}

export function completionH2WordCount(completions: CompletionMap | null | undefined): number {
    return textCompletions(completions?.h2, 'h2')
        .reduce((sum, row) => sum + sectionWordCount(row.value1, row.value2), 0);
}
